import { dataApp } from '../../config/firebase';
import { sopCache } from '../sopCache';
import { firestoreWrapper } from '../firestoreWrapper';
import { SOPWithSummary } from '../../types/sopSummary';
import { sopSummaryService } from '../sopSummaryService';

export interface SOP {
  id: string;
  title: string;
  department: string;
  link: string;
  dateCreated: string;
  author1?: string;
  author2?: string;
  tags?: string[];
  flaggedForReview?: boolean;
  flagReason?: string;
  flaggedBy?: string;
  flaggedAt?: string;
}

export interface SOPMetrics {
  totalSOPs: number;
  departments: number;
  authors: number;
  latestSOP: string;
}

class SOPService {
  private static instance: SOPService;

  private constructor() {}

  static getInstance(): SOPService {
    if (!SOPService.instance) {
      SOPService.instance = new SOPService();
    }
    return SOPService.instance;
  }

  async getSOPs(): Promise<SOP[]> {
    try {
      // Use the enhanced cache implementation
      return await sopCache.getSOPs();
    } catch (error) {
      console.error('Error fetching SOPs:', error);
      throw new Error('Failed to fetch SOPs');
    }
  }

  async getSyncStatus(): Promise<{
    lastSync: Date | null;
    inProgress: boolean;
    lastError: string | null;
    lastSyncSuccess?: boolean;
    processedCount?: number;
    totalSOPs?: number;
    latestEntryDate?: string;
    weeklyEntryCount?: number;
    departmentCount?: number;
    authorCount?: number;
  }> {
    try {
      const syncDoc = await firestoreWrapper.getDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('sopSync'),
          'latest'
        )
      );
      if (syncDoc.exists()) {
        const data = syncDoc.data();
        return {
          // Handle both timestamp and ISO string formats for backward compatibility
          lastSync: data.lastSync 
            ? new Date(data.lastSync.toDate ? data.lastSync.toDate() : data.lastSync) 
            : data.lastSyncISO 
              ? new Date(data.lastSyncISO) 
              : null,
          inProgress: data.inProgress || false,
          lastError: data.error || null,
          lastSyncSuccess: data.lastSyncSuccess,
          processedCount: data.processedCount,
          totalSOPs: data.totalSOPs,
          latestEntryDate: data.latestEntryDate,
          weeklyEntryCount: data.weeklyEntryCount,
          departmentCount: data.departmentCount,
          authorCount: data.authorCount
        };
      }
      return {
        lastSync: null,
        inProgress: false,
        lastError: null
      };
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return {
        lastSync: null,
        inProgress: false,
        lastError: 'Failed to fetch sync status'
      };
    }
  }

  async forceSync(): Promise<{ success: boolean; message: string }> {
    try {
      // Call the Netlify function to force a sync
      const response = await fetch('/.netlify/functions/sops/sync/force', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to sync: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Clear the cache after sync
      sopCache.clearCache();
      
      return {
        success: true,
        message: `Sync completed successfully. Processed ${result.entriesCount} entries.`
      };
    } catch (error) {
      console.error('Error forcing sync:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async getSOPMetrics(): Promise<SOPMetrics> {
    try {
      const sops = await this.getSOPs();
      
      // Calculate metrics
      const departments = new Set();
      const authors = new Set();
      let latestDate = new Date(0);

      sops.forEach(sop => {
        if (sop.department) departments.add(sop.department);
        if (sop.author1) authors.add(sop.author1);
        if (sop.author2) authors.add(sop.author2);
        
        const sopDate = new Date(sop.dateCreated);
        if (sopDate > latestDate) {
          latestDate = sopDate;
        }
      });

      return {
        totalSOPs: sops.length,
        departments: departments.size,
        authors: authors.size,
        latestSOP: latestDate.getTime() > 0 
          ? latestDate.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit' 
            })
          : '-'
      };
    } catch (error) {
      console.error('Error fetching SOP metrics:', error);
      throw new Error('Failed to fetch SOP metrics');
    }
  }

  async getSOPsByDepartment(department: string): Promise<SOP[]> {
    try {
      const sops = await this.getSOPs();
      return sops.filter(sop => sop.department === department);
    } catch (error) {
      console.error('Error fetching SOPs by department:', error);
      throw new Error('Failed to fetch SOPs by department');
    }
  }

  async getSOPsByAuthor(author: string): Promise<SOP[]> {
    try {
      const sops = await this.getSOPs();
      return sops.filter(sop => 
        sop.author1 === author || sop.author2 === author
      );
    } catch (error) {
      console.error('Error fetching SOPs by author:', error);
      throw new Error('Failed to fetch SOPs by author');
    }
  }

  /**
   * Get SOPs with summaries
   * @returns Array of SOPs with summaries
   */
  async getSOPsWithSummaries(): Promise<SOPWithSummary[]> {
    try {
      const sops = await this.getSOPs();
      return await sopSummaryService.enrichSOPsWithSummaries(sops);
    } catch (error) {
      console.error('Error fetching SOPs with summaries:', error);
      throw new Error('Failed to fetch SOPs with summaries');
    }
  }

  /**
   * Generate summaries for SOPs
   * @param params Parameters for generating summaries
   * @returns Result of the generation process
   */
  async generateSOPSummaries(params: {
    batchSize?: number;
    startIndex?: number;
    testMode?: boolean;
    sopIds?: string[];
    onlyMissing?: boolean;
  }) {
    try {
      return await sopSummaryService.generateSummaries(params);
    } catch (error) {
      console.error('Error generating SOP summaries:', error);
      throw new Error('Failed to generate SOP summaries');
    }
  }

  /**
   * Get all SOPs that have been flagged for review
   * @returns Promise<SOP[]> Array of flagged SOPs
   */
  async getFlaggedSOPs(): Promise<SOP[]> {
    try {
      const sops = await this.getSOPs();
      return sops.filter(sop => sop.flaggedForReview === true);
    } catch (error) {
      console.error('Error fetching flagged SOPs:', error);
      throw new Error('Failed to fetch flagged SOPs');
    }
  }
}

export const sopService = SOPService.getInstance();
export default sopService;
